import { settingsConfig } from '../../views/settings/SettingsConfig';
import { SideBarNavItemType } from 'types/NavigationTypes';
import { hasAnyAllowedRight } from 'services/permissions/permissionChecks';

// Finds the settings menu item which belongs to the given route
export const findSettingsMenuItem = (route: string): SideBarNavItemType | null => {

    // Loop through the groups to find the item with the given route
    for (const groupConfig of Object.values(settingsConfig)) {
        const foundItem = groupConfig.items.find(item => item.route === route);
        if (foundItem) {
            return foundItem;
        }
    }

    return null;
};

// Finds the settings menu route to land on after a feature is switched off
export const findPreviousSettingsRoute = (
    currentRoute: string,
    disabledFeature: string,
    packageFeatures: string[],
    activeFeatures: string[],
    allowedRights: string[],
    environmentType: string,
    isEnvironmentSelected: boolean,
    viewOnlyRestricted: boolean = false
): string | null => {

    // Treat the disabled feature as inactive for the visibility check
    const effectiveActiveFeatures = activeFeatures.filter(feature => feature !== disabledFeature);

    // Track the closest visible route before and after the current route
    let previousRoute: string | null = null;
    let nextRoute: string | null = null;
    let currentRouteFound = false;

    // Loop through the menu groups in menu order
    for (const groupConfig of Object.values(settingsConfig)) {

        // Skip the groups which are hidden for the environment type
        if (groupConfig.hideForEnvironmentType?.includes(environmentType)) {
            continue;
        }

        // Skip the groups which the user has no permission for
        if (groupConfig.permissionCheck && !hasAnyAllowedRight(allowedRights, groupConfig.permissionCheck)) {
            continue;
        }

        // Loop through the menu items of the group
        for (const item of groupConfig.items) {

            // Mark the current route when it is found
            if (item.route === currentRoute) {
                currentRouteFound = true;
                continue;
            }

            // Determine the visibility of the item like the settings menu does
            const hasPackageFeature = !item.requiredFeature || packageFeatures.includes(item.requiredFeature);
            const hasFeatureEnabled = !item.requiredFeature || effectiveActiveFeatures.includes(item.requiredFeature);
            const isVisibleByFeature = item.showWhenDisabled ? hasPackageFeature : (hasPackageFeature && hasFeatureEnabled);

            // Skip environment wide items when another division is selected
            if (item.environmentWide && !isEnvironmentSelected) {
                continue;
            }

            // Skip items which are hidden for the view only package
            if (item.hideForViewOnlyPackage && viewOnlyRestricted) {
                continue;
            }

            // Skip items which require a right the user does not have
            if (item.permissionCheck && !hasAnyAllowedRight(allowedRights, item.permissionCheck)) {
                continue;
            }

            // Skip items which are not visible in the menu
            if (!isVisibleByFeature) {
                continue;
            }

            // Track the item as previous route until the current route is found
            if (!currentRouteFound) {
                previousRoute = item.route;

            // Otherwise track the first visible route after the current route
            } else if (!nextRoute) {
                nextRoute = item.route;
            }
        }
    }

    // Prefer the route above the current item and fall back to the one below
    return previousRoute || nextRoute;
};
